import type { FC, MouseEvent, ReactNode } from 'react'

import { useState } from 'react'
import { useRouter } from 'next/router'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { scrollToSection } from '@/lib/scroll'
import { useActiveSection } from '@/hooks/use-active-section'
import { useScrolled } from '@/hooks/use-scrolled'
import { Button } from '@/components/ui/button'
import { Wordmark } from '@/components/generals/wordmark'
import { CtaLink } from '@/components/generals/cta-link'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

import { brand, contact, navItems } from '@/constants/content'

const sectionIds = navItems.map(item => item.sectionId)

export const SiteHeader: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const isHome = pathname === '/'
    const scrolled = useScrolled()
    const activeId = useActiveSection(sectionIds)
    const [menuOpen, setMenuOpen] = useState(false)

    const hrefFor = (sectionId: string): string => (isHome ? `#${sectionId}` : `/#${sectionId}`)

    const handleMenuLink = (event: MouseEvent<HTMLAnchorElement>, sectionId: string): void => {
        if (!isHome) return

        event.preventDefault()
        setMenuOpen(false)
        window.setTimeout(() => scrollToSection(sectionId), 220)
    }

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-40 h-(--header-height) transition-colors duration-300',
                scrolled || menuOpen
                    ? 'border-b border-soft-white/10 bg-navy-900/85 backdrop-blur-md'
                    : 'border-b border-transparent bg-transparent'
            )}
        >
            <div className='container-site flex h-full items-center justify-between gap-6'>
                <a
                    href={isHome ? '#top' : '/'}
                    aria-label={`${brand.name}, back to the top`}
                    className='-mx-1 inline-flex min-h-12 items-center rounded-lg px-1'
                >
                    <Wordmark />
                </a>

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {navItems.map(item => {
                            const active = isHome && activeId === item.sectionId

                            return (
                                <li key={item.sectionId}>
                                    <a
                                        href={hrefFor(item.sectionId)}
                                        aria-current={active ? 'location' : undefined}
                                        className={cn(
                                            'relative inline-flex min-h-11 items-center rounded-lg px-3.5 text-sm font-medium transition-colors',
                                            active ? 'text-soft-white' : 'text-cool-grey-light hover:text-soft-white'
                                        )}
                                    >
                                        {item.label}

                                        <span
                                            aria-hidden
                                            className={cn(
                                                'absolute inset-x-3.5 -bottom-0.5 h-0.5 rounded-full bg-orange transition-opacity duration-300',
                                                active ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                    </a>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <CtaLink
                        href={hrefFor('contact')}
                        size='md'
                        aria-label={contact.sectionCta}
                        className='hidden sm:inline-flex'
                    >
                        {contact.sectionCta}
                    </CtaLink>

                    <CtaLink
                        href={hrefFor('contact')}
                        size='md'
                        aria-label={contact.sectionCta}
                        className='sm:hidden'
                    >
                        Contact
                    </CtaLink>

                    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                        <SheetTrigger
                            render={
                                <Button
                                    variant='ghost'
                                    size='icon-lg'
                                    aria-label='Open menu'
                                    className='text-soft-white lg:hidden'
                                />
                            }
                        >
                            <Menu />
                        </SheetTrigger>

                        <SheetContent side='right' className='border-soft-white/10 bg-navy-900 px-6 pt-5 pb-8'>
                            <SheetTitle className='flex min-h-12 items-center'>
                                <Wordmark />
                            </SheetTitle>

                            <SheetDescription className='sr-only'>
                                Site navigation
                            </SheetDescription>

                            <nav aria-label='Mobile' className='mt-4'>
                                <ul className='flex flex-col gap-1'>
                                    {navItems.map(item => (
                                        <li key={item.sectionId}>
                                            <a
                                                href={hrefFor(item.sectionId)}
                                                onClick={event => handleMenuLink(event, item.sectionId)}
                                                className={cn(
                                                    'flex min-h-14 items-center rounded-xl px-4 font-heading text-2xl font-semibold transition-colors',
                                                    isHome && activeId === item.sectionId
                                                        ? 'bg-navy-700 text-soft-white'
                                                        : 'text-cool-grey-light hover:bg-navy-800 hover:text-soft-white'
                                                )}
                                            >
                                                {item.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className='mt-auto flex flex-col gap-3'>
                                <CtaLink
                                    href={hrefFor('contact')}
                                    onClick={event => handleMenuLink(event, 'contact')}
                                    className='w-full'
                                >
                                    {contact.sectionCta}
                                </CtaLink>

                                <p className='text-center text-xs font-semibold tracking-[0.18em] text-orange'>
                                    {brand.phrase}
                                </p>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    )
}
